<template>
  <div class="terms-of-service-container">
    <el-main>
      <div class="terms-content">
        <h1 class="terms-title">Terms of Service</h1>
        <p class="terms-date">Last Updated: June 2025</p>
        <p class="terms-operator">Operated By: Extracurricular Activity Excellence International Pte. Ltd.</p>
        <p class="terms-effective">Effective Date: June 2025</p>

        <section class="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>By accessing or using LearningFirst (the "Service"), you agree to these Terms of Service ("Terms") and our Privacy Policy. If you do not agree, do not use the Service.</p>
        </section>

        <section class="terms-section">
          <h2>2. Eligibility & Minors</h2>
          <p>You must:</p>
          <ul>
            <li>Be at least 13 years old to create an account. Users under 13 are not permitted to use the Service.</li>
            <li>If you are under 18 years old, you must have the consent of a parent or legal guardian to use the Service. By allowing your child to use the Service, the parent or guardian agrees to these Terms on behalf of the minor and assumes responsibility for the minor's use of the Service.</li>
            <li>If you are under 18, any purchases, subscriptions, or payment transactions must be approved and made by a parent or legal guardian. We are not liable for unauthorized purchases made by minors without parental consent.</li>
            <li>Not be barred from receiving services under Singaporean or international laws.</li>
          </ul>
          <p><strong>Children's Data Protection:</strong> In accordance with Singapore's Personal Data Protection Act (PDPA) and the PDPC Advisory Guidelines on Children's Personal Data, we treat all personal data of users under 18 as sensitive and apply a higher standard of care. We practise data minimisation and do not make minors' account information publicly searchable by default. For users under 13, verifiable parental or guardian consent is required before any personal data is collected. For full details, please refer to our Privacy Policy.</p>
        </section>

        <section class="terms-section">
          <h2>3. Account Registration</h2>
          <ul>
            <li><strong>OTP Login:</strong> Your email is your account ID; OTPs are sent for authentication.</li>
            <li><strong>Security:</strong> You are responsible for safeguarding your email/device. Notify us immediately of unauthorized use.</li>
            <li><strong>Accuracy:</strong> Provide truthful, current information (e.g., for payments).</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>4. Service Description</h2>
          <p>LearningFirst enables:</p>
          <ul>
            <li>AI-powered chat and content generation.</li>
            <li>Creation, sharing, and monetization of user-generated content ("UGC").</li>
            <li>Interactions with other users (e.g., comments, subscriptions).</li>
            <li>Paid access to exclusive content/subscriptions.</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>5. User Responsibilities</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Upload illegal, abusive, or infringing content (e.g., hate speech, pirated material).</li>
            <li>Harass, impersonate, or exploit others.</li>
            <li>Reverse-engineer, spam, or disrupt the Service.</li>
            <li>Bypass payment systems or initiate fraudulent transactions.</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>6. Intellectual Property</h2>
          <ul>
            <li><strong>Your UGC:</strong> You retain ownership but grant us a global, royalty-free license to host, display, and distribute it via the Service.</li>
            <li><strong>AI Output:</strong> Content generated using our AI tools is owned by you, subject to these Terms.</li>
            <li><strong>Our IP:</strong> All Service-related code, logos, and designs are owned by Extracurricular Activity Excellence International Pte. Ltd. (or licensors).</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>7. Payments & Subscriptions</h2>
          <ul>
            <li><strong>Purchases:</strong> Payments for content/subscriptions are processed via third-party gateways (e.g., Stripe).</li>
            <li><strong>Fees:</strong> Prices are displayed in USD; you authorize recurring charges for subscriptions.</li>
            <li><strong>Refunds:</strong> Generally not provided unless required by law. Disputes must be raised within 7 days.</li>
            <li><strong>Taxes:</strong> You are responsible for applicable taxes (e.g., GST in Singapore).</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>8. Termination</h2>
          <ul>
            <li><strong>By You:</strong> Delete your account anytime via settings.</li>
            <li><strong>By Us:</strong> We may suspend/terminate accounts for breaches, fraud, or inactivity.</li>
            <li><strong>Effect:</strong> Termination voids access; clauses on IP, payments, and liability survive.</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>9. Disclaimers</h2>
          <ul>
            <li><strong>Service "AS IS":</strong> We do not guarantee error-free, uninterrupted, or accurate Service (including AI outputs).</li>
            <li><strong>UGC:</strong> We are not liable for user content or third-party links.</li>
            <li><strong>Financial Risk:</strong> Payments for UGC are at your discretion; we do not endorse user offerings.</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>10. Limitation of Liability</h2>
          <p>To the maximum extent permitted by Singapore law (Consumer Protection Act, PDPA):</p>
          <ul>
            <li>We exclude liability for indirect, consequential, or punitive damages.</li>
            <li>Our total liability for any claim is capped at SGD 100 or fees paid in the past 6 months.</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>11. Indemnification</h2>
          <p>You agree to indemnify us against claims arising from:</p>
          <ul>
            <li>Your UGC or conduct.</li>
            <li>Payment disputes or breaches of these Terms.</li>
            <li>Violations of third-party rights (e.g., copyright infringement).</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>12. Dispute Resolution & Governing Law</h2>
          <ul>
            <li><strong>Governing Law:</strong> Singaporean law (excluding conflicts principles).</li>
            <li><strong>Negotiation First:</strong> Parties must attempt good-faith resolution within 30 days.</li>
            <li><strong>Arbitration:</strong> Unresolved disputes go to binding arbitration in Singapore (SIAC Rules). Class actions waived.</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2>13. Modifications</h2>
          <p>We may update these Terms periodically. Continued use after changes constitutes acceptance. Material updates will be notified via email or in-Service alerts.</p>
        </section>

        <section class="terms-section">
          <h2>14. Contact</h2>
          <p>For notices or queries:</p>
          <div class="contact-info">
            <p><strong>Extracurricular Activity Excellence International Pte. Ltd.</strong></p>
            <p>Email: <a href="mailto:contact@eae.international">contact@eae.international</a></p>
          </div>
        </section>
      </div>
    </el-main>
  </div>
</template>

<script>
export default {
  name: 'TermsOfService',
  meta: {
    title: 'Terms of Service - LearningFirst'
  }
}
</script>

<style scoped>
.terms-of-service-container {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-top: 60px;
}

.terms-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.6;
  color: #303133;
}

.terms-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1159AF;
  margin-bottom: 10px;
  text-align: center;
}

.terms-date {
  font-size: 1.1rem;
  color: #606266;
  text-align: center;
  margin-bottom: 5px;
}

.terms-operator {
  font-size: 1.1rem;
  color: #606266;
  text-align: center;
  margin-bottom: 5px;
  font-weight: 500;
}

.terms-effective {
  font-size: 1.1rem;
  color: #606266;
  text-align: center;
  margin-bottom: 40px;
}

.terms-section {
  margin-bottom: 40px;
}

.terms-section h2 {
  font-size: 1.8rem;
  font-weight: bold;
  color: #1159AF;
  margin-bottom: 15px;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 8px;
}

.terms-section p {
  margin-bottom: 15px;
  font-size: 1rem;
  color: #606266;
}

.terms-section ul {
  margin: 15px 0;
  padding-left: 20px;
}

.terms-section li {
  margin-bottom: 8px;
  color: #606266;
}

.terms-section strong {
  color: #303133;
  font-weight: 600;
}

.terms-section a {
  color: #409EFF;
  text-decoration: none;
}

.terms-section a:hover {
  text-decoration: underline;
}

.contact-info {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 6px;
  border-left: 4px solid #409EFF;
  margin-top: 15px;
}

.contact-info p {
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .terms-content {
    padding: 20px 15px;
    margin: 0 10px;
  }
  
  .terms-title {
    font-size: 2rem;
  }
  
  .terms-section h2 {
    font-size: 1.5rem;
  }
}
</style> 